import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { AppShell } from '../components/AppShell';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent } from '../components/ui/card';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import {
  Sheet, SheetContent, SheetHeader, SheetTitle,
} from '../components/ui/sheet';
import {
  ArrowLeft, Plus, Loader2, Trash2, FileDown, Check, Camera, X,
  Mail, MapPin, Cloud, HardHat, ClipboardList, Wrench,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const SEVERITY_LABEL = { low: 'Faible', medium: 'Moyenne', high: 'Élevée', critical: 'CRITIQUE' };
const SEVERITY_STYLE = {
  low: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  medium: 'bg-amber-50 text-amber-700 border-amber-200',
  high: 'bg-red-50 text-red-700 border-red-200',
  critical: 'bg-red-900 text-white border-red-900',
};
const PHOTO_CATS = [
  { value: 'general', label: 'Observation générale' },
  { value: 'non_conformity', label: 'Non-conformité' },
  { value: 'safety', label: 'Sécurité' },
  { value: 'progress', label: 'Avancement' },
];

export function SiteVisitsPage() {
  const { chantierId } = useParams();
  const navigate = useNavigate();
  const [chantier, setChantier] = useState(null);
  const [visits, setVisits] = useState([]);
  const [entreprises, setEntreprises] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState(null);
  const [sendDialogVisit, setSendDialogVisit] = useState(null);

  const load = useCallback(async () => {
    try {
      const [cRes, vRes, eRes] = await Promise.all([
        axios.get(`${API}/chantiers/${chantierId}`, { withCredentials: true }),
        axios.get(`${API}/site-visits?chantier_id=${chantierId}`, { withCredentials: true }),
        axios.get(`${API}/chantiers/${chantierId}/entreprises`, { withCredentials: true }),
      ]);
      setChantier(cRes.data);
      setVisits(vRes.data || []);
      setEntreprises(eRes.data || []);
    } catch { toast.error('Erreur de chargement'); }
    finally { setLoading(false); }
  }, [chantierId]);

  useEffect(() => { load(); }, [load]);

  const create = async () => {
    setCreating(true);
    try {
      const r = await axios.post(`${API}/site-visits`, {
        chantier_id: chantierId,
        visit_date: new Date().toISOString().slice(0, 10),
        visit_time: new Date().toTimeString().slice(0, 5),
      }, { withCredentials: true });
      toast.success(`Visite n°${r.data.visit_number} créée`);
      setEditing(r.data);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
    finally { setCreating(false); }
  };

  const del = async (id) => {
    if (!window.confirm('Supprimer cette visite (et ses photos) ?')) return;
    try {
      await axios.delete(`${API}/site-visits/${id}`, { withCredentials: true });
      toast.success('Visite supprimée');
      load();
    } catch { toast.error('Erreur'); }
  };

  const downloadPDF = async (id, num) => {
    try {
      const r = await axios.get(`${API}/site-visits/${id}/pdf`, { withCredentials: true, responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a'); a.href = url; a.download = `Visite_chantier_${num}.pdf`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch { toast.error('Erreur PDF'); }
  };

  if (loading) return (
    <AppShell>
      <div className="flex items-center justify-center h-64"><Loader2 className="h-8 w-8 animate-spin text-[#0055FF]" /></div>
    </AppShell>
  );

  return (
    <AppShell>
      <div className="space-y-5 max-w-6xl mx-auto" data-testid="site-visits-page">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate(`/chantiers/${chantierId}`)} data-testid="visits-back-btn">
            <ArrowLeft size={20} />
          </Button>
          <div className="flex-1 min-w-0">
            <h1 className="text-xl sm:text-2xl font-bold text-[#111827] truncate">
              Visites de chantier
            </h1>
            <p className="text-sm text-[#6B7280] truncate">
              {chantier?.name} · {visits.length} visite{visits.length !== 1 ? 's' : ''}
            </p>
          </div>
          <Button onClick={create} disabled={creating} className="bg-[#0055FF] hover:bg-blue-700 h-10" data-testid="new-visit-btn">
            {creating ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Plus size={16} className="mr-1.5" />}
            Nouvelle visite
          </Button>
        </div>

        {visits.length === 0 ? (
          <Card className="border-dashed border-2 border-[#D1D5DB]">
            <CardContent className="p-10 text-center">
              <HardHat size={40} className="mx-auto text-[#9CA3AF] mb-3" />
              <p className="text-sm text-[#6B7280]">Aucune visite enregistrée sur ce chantier</p>
              <p className="text-xs text-[#9CA3AF] mt-1">
                Créez votre première visite pour documenter observations, non-conformités et actions terrain.
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {visits.map(v => (
              <Card key={v.id} className="hover:shadow-sm transition-shadow" data-testid={`visit-row-${v.id}`}>
                <CardContent className="p-4">
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-bold text-[#111827]">Visite n°{v.visit_number}</span>
                        <span className={`text-xs px-2 py-0.5 rounded-full ${v.status === 'finalized' ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-600'}`}>
                          {v.status === 'finalized' ? '✓ Finalisée' : 'Brouillon'}
                        </span>
                        {(v.non_conformities || []).length > 0 && (
                          <span className="text-xs px-2 py-0.5 rounded-full bg-red-50 text-red-700 border border-red-200">
                            ⚠️ {v.non_conformities.length} NC
                          </span>
                        )}
                      </div>
                      <div className="text-sm text-[#4B5563] flex flex-wrap gap-x-4 gap-y-1">
                        <span>📅 {v.visit_date} {v.visit_time && `· ${v.visit_time}`}</span>
                        {v.weather && <span>☁️ {v.weather}</span>}
                        {v.phase && <span>🏗️ {v.phase}</span>}
                        {v.progress_percent !== null && v.progress_percent !== undefined && (
                          <span>📊 {v.progress_percent}%</span>
                        )}
                      </div>
                      {v.general_observations && (
                        <p className="text-xs text-[#6B7280] mt-1.5 line-clamp-2">{v.general_observations}</p>
                      )}
                    </div>
                    <div className="flex gap-1 flex-wrap justify-end">
                      <Button size="sm" variant="outline" onClick={() => setEditing(v)} data-testid={`edit-visit-${v.id}`}>
                        {v.status === 'finalized' ? 'Consulter' : 'Modifier'}
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => downloadPDF(v.id, v.visit_number)} data-testid={`pdf-visit-${v.id}`}>
                        <FileDown size={14} className="mr-1" /> PDF
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => setSendDialogVisit(v)} data-testid={`send-visit-${v.id}`}
                        className="border-[#0055FF] text-[#0055FF] hover:bg-blue-50">
                        <Mail size={14} className="mr-1" /> Envoyer
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => del(v.id)}
                        className="text-red-500 hover:bg-red-50" data-testid={`del-visit-${v.id}`}>
                        <Trash2 size={14} />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {editing && (
        <SiteVisitEditor
          visit={editing}
          onClose={() => { setEditing(null); load(); }}
        />
      )}

      {sendDialogVisit && (
        <SendVisitDialog
          visit={sendDialogVisit}
          entreprises={entreprises}
          onClose={() => setSendDialogVisit(null)}
        />
      )}
    </AppShell>
  );
}

/* ==================================================================
   EDITOR
   ================================================================== */
function SiteVisitEditor({ visit: initialVisit, onClose }) {
  const [visit, setVisit] = useState(initialVisit);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const readonly = visit.status === 'finalized';

  const reload = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/site-visits/${initialVisit.id}`, { withCredentials: true });
      setVisit(r.data);
    } catch { /* ignore */ }
  }, [initialVisit.id]);

  useEffect(() => { reload(); }, [reload]);

  const setField = (k, v) => setVisit(prev => ({ ...prev, [k]: v }));

  const save = async (finalize = false) => {
    setSaving(true);
    try {
      const payload = {
        visit_date: visit.visit_date, visit_time: visit.visit_time,
        weather: visit.weather, phase: visit.phase, progress_percent: visit.progress_percent,
        people_met: visit.people_met, general_observations: visit.general_observations,
        non_conformities: visit.non_conformities || [],
        safety_observations: visit.safety_observations,
        delays: visit.delays || [], actions: visit.actions || [],
        next_visit: visit.next_visit || null,
        visitor_name: visit.visitor_name, visitor_role: visit.visitor_role,
      };
      await axios.put(`${API}/site-visits/${visit.id}`, payload, { withCredentials: true });
      if (finalize) {
        await axios.post(`${API}/site-visits/${visit.id}/finalize`, {}, { withCredentials: true });
        toast.success('Visite finalisée');
      } else {
        toast.success('Enregistrée');
      }
      onClose();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
    finally { setSaving(false); }
  };

  const uploadPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append('file', file);
    try {
      await axios.post(
        `${API}/upload?chantier_id=${visit.chantier_id}&visit_id=${visit.id}&category=general`,
        fd,
        { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } }
      );
      toast.success('Photo ajoutée');
      reload();
    } catch (err) { toast.error('Erreur upload'); }
    finally { setUploading(false); e.target.value = ''; }
  };

  const updatePhotoCat = async (photoId, category, caption) => {
    // Note: pas d'endpoint dédié pour renommer une photo — on garde simple pour MVP
    setVisit(prev => ({
      ...prev,
      photos: (prev.photos || []).map(p => p.id === photoId ? { ...p, category, caption } : p)
    }));
  };

  const addNC = () => setField('non_conformities', [
    ...(visit.non_conformities || []),
    { topic: '', location: '', severity: 'medium', action_required: '', responsable: '', deadline: '' }
  ]);
  const rmNC = (i) => setField('non_conformities', (visit.non_conformities || []).filter((_, j) => j !== i));
  const updNC = (i, patch) => setField('non_conformities', (visit.non_conformities || []).map((nc, j) => j === i ? { ...nc, ...patch } : nc));

  const addAction = () => setField('actions', [...(visit.actions || []), { topic: '', responsable: '', deadline: '', done: false }]);
  const rmAction = (i) => setField('actions', (visit.actions || []).filter((_, j) => j !== i));
  const updAction = (i, patch) => setField('actions', (visit.actions || []).map((a, j) => j === i ? { ...a, ...patch } : a));

  const addDelay = () => setField('delays', [...(visit.delays || []), { lot_name: '', delay_days: 0, reason: '' }]);
  const rmDelay = (i) => setField('delays', (visit.delays || []).filter((_, j) => j !== i));
  const updDelay = (i, patch) => setField('delays', (visit.delays || []).map((d, j) => j === i ? { ...d, ...patch } : d));

  return (
    <Sheet open onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="w-full sm:max-w-3xl overflow-y-auto" aria-describedby="visit-desc">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <HardHat className="text-[#0055FF]" size={22} />
            Visite n°{visit.visit_number}
            {readonly && <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700">Finalisée</span>}
          </SheetTitle>
          <p id="visit-desc" className="text-sm text-[#6B7280]">Complétez toutes les sections pertinentes</p>
        </SheetHeader>

        <div className="space-y-6 mt-5">
          {/* HEADER INFO */}
          <section className="grid grid-cols-2 gap-3">
            <div><Label>Date *</Label>
              <Input type="date" value={visit.visit_date} onChange={e => setField('visit_date', e.target.value)} disabled={readonly} data-testid="visit-date" /></div>
            <div><Label>Heure</Label>
              <Input type="time" value={visit.visit_time || ''} onChange={e => setField('visit_time', e.target.value)} disabled={readonly} /></div>
            <div><Label>Météo</Label>
              <Input placeholder="Ex: Ensoleillé 28°C" value={visit.weather || ''} onChange={e => setField('weather', e.target.value)} disabled={readonly} /></div>
            <div><Label>Phase</Label>
              <Input placeholder="Ex: Gros œuvre" value={visit.phase || ''} onChange={e => setField('phase', e.target.value)} disabled={readonly} /></div>
            <div><Label>Avancement global (%)</Label>
              <Input type="number" min={0} max={100} value={visit.progress_percent ?? ''} onChange={e => setField('progress_percent', e.target.value === '' ? null : parseInt(e.target.value))} disabled={readonly} /></div>
            <div><Label>Rôle du visiteur</Label>
              <Select value={visit.visitor_role || 'OPC'} onValueChange={(v) => setField('visitor_role', v)} disabled={readonly}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {['OPC', 'MOE', 'CSPS', 'MO', 'AMO', 'BET', 'CT'].map(r => (
                    <SelectItem key={r} value={r}>{r}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-2"><Label>Nom du visiteur</Label>
              <Input value={visit.visitor_name || ''} onChange={e => setField('visitor_name', e.target.value)} disabled={readonly} /></div>
          </section>

          {/* PEOPLE MET */}
          <section>
            <Label className="flex items-center gap-1.5"><MapPin size={14} /> Personnes rencontrées sur place</Label>
            <Textarea rows={2} placeholder="Ex: M. Dupont (chef de chantier Bouygues), Mme Martin (électricien)"
              value={visit.people_met || ''} onChange={e => setField('people_met', e.target.value)} disabled={readonly} />
          </section>

          {/* GENERAL OBSERVATIONS */}
          <section>
            <Label className="flex items-center gap-1.5"><ClipboardList size={14} /> Observations générales</Label>
            <Textarea rows={3} placeholder="Progression, qualité d'exécution, propreté, cadence…"
              value={visit.general_observations || ''} onChange={e => setField('general_observations', e.target.value)} disabled={readonly} />
          </section>

          {/* NON-CONFORMITIES */}
          <section>
            <div className="flex items-center justify-between mb-2">
              <Label className="text-red-700 font-semibold">Non-conformités relevées ({(visit.non_conformities || []).length})</Label>
              {!readonly && <Button size="sm" variant="outline" onClick={addNC} data-testid="add-nc"><Plus size={14} className="mr-1" />Ajouter</Button>}
            </div>
            <div className="space-y-2">
              {(visit.non_conformities || []).map((nc, i) => (
                <div key={i} className={`p-3 rounded-lg border ${SEVERITY_STYLE[nc.severity] || 'bg-gray-50 border-gray-200'}`}>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    <Input placeholder="Titre *" value={nc.topic} onChange={e => updNC(i, { topic: e.target.value })} disabled={readonly} className="md:col-span-2" />
                    <Input placeholder="Localisation" value={nc.location} onChange={e => updNC(i, { location: e.target.value })} disabled={readonly} />
                    <Select value={nc.severity} onValueChange={v => updNC(i, { severity: v })} disabled={readonly}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {Object.entries(SEVERITY_LABEL).map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    <Input placeholder="Action requise" value={nc.action_required} onChange={e => updNC(i, { action_required: e.target.value })} disabled={readonly} className="md:col-span-2" />
                    <Input placeholder="Responsable" value={nc.responsable} onChange={e => updNC(i, { responsable: e.target.value })} disabled={readonly} />
                    <Input type="date" placeholder="Échéance" value={nc.deadline} onChange={e => updNC(i, { deadline: e.target.value })} disabled={readonly} />
                  </div>
                  {!readonly && (
                    <Button size="sm" variant="ghost" onClick={() => rmNC(i)} className="mt-1 text-red-600 hover:bg-red-100">
                      <X size={14} className="mr-1" /> Retirer
                    </Button>
                  )}
                </div>
              ))}
            </div>
          </section>

          {/* SAFETY */}
          <section>
            <Label className="flex items-center gap-1.5 text-orange-700 font-semibold">
              <HardHat size={14} /> Sécurité (EPI, protections, PGC, signalisation)
            </Label>
            <Textarea rows={3} placeholder="Ex: EPI conformes, protection collective R+2 OK, panneaux d'accès dégradés à remplacer…"
              value={visit.safety_observations || ''} onChange={e => setField('safety_observations', e.target.value)} disabled={readonly} />
          </section>

          {/* PHOTOS */}
          <section>
            <div className="flex items-center justify-between mb-2">
              <Label className="flex items-center gap-1.5">
                <Camera size={14} /> Photos annexées ({(visit.photos || []).length})
              </Label>
              {!readonly && (
                <label className="inline-flex items-center gap-2 cursor-pointer bg-[#0055FF] text-white text-sm font-medium px-3 py-1.5 rounded-lg hover:bg-blue-700">
                  {uploading ? <Loader2 size={14} className="animate-spin" /> : <Camera size={14} />}
                  Ajouter photo
                  <input type="file" accept="image/*" className="hidden" onChange={uploadPhoto} data-testid="visit-photo-input" />
                </label>
              )}
            </div>
            {(visit.photos || []).length === 0 ? (
              <p className="text-xs text-[#9CA3AF] italic">Aucune photo pour l&apos;instant</p>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {visit.photos.map(p => (
                  <div key={p.id} className="border rounded-lg p-2 bg-white">
                    <img src={`${API}/files/${p.id}/download`} alt={p.original_filename}
                      className="w-full h-32 object-cover rounded" />
                    <Select value={p.category || 'general'}
                      onValueChange={v => updatePhotoCat(p.id, v, p.caption)}
                      disabled={readonly}>
                      <SelectTrigger className="mt-1 h-8 text-xs"><SelectValue /></SelectTrigger>
                      <SelectContent>{PHOTO_CATS.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}</SelectContent>
                    </Select>
                    <Input placeholder="Légende" value={p.caption || ''}
                      onChange={e => updatePhotoCat(p.id, p.category, e.target.value)}
                      disabled={readonly} className="mt-1 h-7 text-xs" />
                  </div>
                ))}
              </div>
            )}
          </section>

          {/* DELAYS */}
          <section>
            <div className="flex items-center justify-between mb-2">
              <Label>Retards / avance par lot ({(visit.delays || []).length})</Label>
              {!readonly && <Button size="sm" variant="outline" onClick={addDelay} data-testid="add-delay"><Plus size={14} className="mr-1" />Ajouter</Button>}
            </div>
            <div className="space-y-2">
              {(visit.delays || []).map((d, i) => (
                <div key={i} className="grid grid-cols-12 gap-2 items-center">
                  <Input placeholder="Lot / Corps d'état" className="col-span-5" value={d.lot_name} onChange={e => updDelay(i, { lot_name: e.target.value })} disabled={readonly} />
                  <Input type="number" placeholder="Jours (+/-)" className="col-span-2" value={d.delay_days} onChange={e => updDelay(i, { delay_days: parseInt(e.target.value || 0) })} disabled={readonly} />
                  <Input placeholder="Motif" className="col-span-4" value={d.reason} onChange={e => updDelay(i, { reason: e.target.value })} disabled={readonly} />
                  {!readonly && <Button size="icon" variant="ghost" className="col-span-1 text-red-500" onClick={() => rmDelay(i)}><X size={14} /></Button>}
                </div>
              ))}
            </div>
          </section>

          {/* ACTIONS */}
          <section>
            <div className="flex items-center justify-between mb-2">
              <Label className="flex items-center gap-1.5"><Wrench size={14} /> Actions à mener ({(visit.actions || []).length})</Label>
              {!readonly && <Button size="sm" variant="outline" onClick={addAction} data-testid="add-action"><Plus size={14} className="mr-1" />Ajouter</Button>}
            </div>
            <div className="space-y-2">
              {(visit.actions || []).map((a, i) => (
                <div key={i} className="p-3 rounded-lg border bg-white space-y-2">
                  <div className="flex items-center gap-2">
                    <Checkbox checked={a.done} onCheckedChange={v => updAction(i, { done: !!v })} disabled={readonly} />
                    <Input placeholder="Action" value={a.topic} onChange={e => updAction(i, { topic: e.target.value })} disabled={readonly} className="flex-1" />
                    {!readonly && <Button size="icon" variant="ghost" className="text-red-500" onClick={() => rmAction(i)}><X size={14} /></Button>}
                  </div>
                  <div className="grid grid-cols-2 gap-2 ml-6">
                    <Input placeholder="Responsable" value={a.responsable} onChange={e => updAction(i, { responsable: e.target.value })} disabled={readonly} />
                    <Input type="date" value={a.deadline} onChange={e => updAction(i, { deadline: e.target.value })} disabled={readonly} />
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* NEXT VISIT */}
          <section>
            <Label className="font-semibold text-[#0055FF]">Prochaine visite programmée</Label>
            <div className="grid grid-cols-2 gap-2 mt-1">
              <Input type="date" placeholder="Date" value={visit.next_visit?.date || ''}
                onChange={e => setField('next_visit', { ...(visit.next_visit || {}), date: e.target.value })} disabled={readonly} />
              <Input type="time" placeholder="Heure" value={visit.next_visit?.time || ''}
                onChange={e => setField('next_visit', { ...(visit.next_visit || {}), time: e.target.value })} disabled={readonly} />
            </div>
            <Input className="mt-2" placeholder="Note (ex: point avancement + vérification GC)"
              value={visit.next_visit?.note || ''}
              onChange={e => setField('next_visit', { ...(visit.next_visit || {}), note: e.target.value })} disabled={readonly} />
          </section>

          {/* ACTIONS */}
          <div className="sticky bottom-0 bg-white border-t pt-3 pb-2 flex gap-2">
            <Button variant="outline" onClick={onClose} className="flex-1" data-testid="visit-close">Fermer</Button>
            {!readonly && (
              <>
                <Button onClick={() => save(false)} disabled={saving} className="flex-1 bg-[#0055FF] hover:bg-blue-700" data-testid="visit-save">
                  {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}Enregistrer
                </Button>
                <Button onClick={() => save(true)} disabled={saving} className="flex-1 bg-emerald-600 hover:bg-emerald-700" data-testid="visit-finalize">
                  <Check size={14} className="mr-1" /> Finaliser
                </Button>
              </>
            )}
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}

/* ==================================================================
   SEND DIALOG - Selection multiple d'entreprises + emails libres
   ================================================================== */
function SendVisitDialog({ visit, entreprises, onClose }) {
  const [selected, setSelected] = useState(new Set());
  const [extra, setExtra] = useState('');
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);

  const entWithEmail = entreprises.filter(e => e.email && e.email.includes('@'));

  const toggle = (email) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(email)) next.delete(email); else next.add(email);
      return next;
    });
  };
  const selectAll = () => setSelected(new Set(entWithEmail.map(e => e.email)));
  const deselectAll = () => setSelected(new Set());

  const send = async () => {
    const extras = extra.split(/[,;\n]/).map(s => s.trim()).filter(s => s && s.includes('@'));
    const recipients = [...new Set([...Array.from(selected), ...extras])];
    if (recipients.length === 0) { toast.error('Au moins un destinataire requis'); return; }
    setSending(true);
    try {
      const r = await axios.post(`${API}/site-visits/${visit.id}/send-email`,
        { recipients, message }, { withCredentials: true });
      toast.success(`Envoyé à ${r.data.sent}/${r.data.total} destinataire(s)`);
      if (r.data.failed?.length) toast.warning(`${r.data.failed.length} envoi(s) en échec`);
      onClose();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur envoi'); }
    finally { setSending(false); }
  };

  return (
    <Sheet open onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="w-full sm:max-w-md overflow-y-auto" aria-describedby="send-desc">
        <SheetHeader>
          <SheetTitle>Envoyer la visite n°{visit.visit_number}</SheetTitle>
          <p id="send-desc" className="text-sm text-[#6B7280]">Cochez les intervenants qui recevront le PDF par email.</p>
        </SheetHeader>

        <div className="space-y-4 mt-5">
          <div>
            <div className="flex items-center justify-between mb-2">
              <Label>Entreprises du chantier ({entWithEmail.length})</Label>
              <div className="flex gap-2 text-xs">
                <button onClick={selectAll} className="text-[#0055FF] hover:underline" data-testid="select-all">Tout cocher</button>
                <span>·</span>
                <button onClick={deselectAll} className="text-[#6B7280] hover:underline" data-testid="deselect-all">Tout décocher</button>
              </div>
            </div>
            {entWithEmail.length === 0 ? (
              <p className="text-xs text-[#9CA3AF] italic">
                Aucune entreprise du chantier n&apos;a d&apos;email enregistré. Ajoutez des emails dans la fiche entreprise ou saisissez-les ci-dessous.
              </p>
            ) : (
              <div className="space-y-1 max-h-60 overflow-y-auto border rounded-lg p-2">
                {entWithEmail.map(e => (
                  <label key={e.id} className="flex items-center gap-2 p-2 hover:bg-gray-50 rounded cursor-pointer">
                    <Checkbox checked={selected.has(e.email)} onCheckedChange={() => toggle(e.email)} data-testid={`recipient-${e.id}`} />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium truncate">{e.nom}
                        {e.corps_etat && <span className="text-xs text-[#9CA3AF] ml-2">{e.corps_etat}</span>}
                      </div>
                      <div className="text-xs text-[#6B7280] truncate">{e.email}</div>
                    </div>
                  </label>
                ))}
              </div>
            )}
          </div>

          <div>
            <Label>Emails supplémentaires</Label>
            <Textarea rows={2} placeholder="jean@moe.fr, marie@bet.fr…" value={extra} onChange={e => setExtra(e.target.value)} data-testid="extra-emails" />
            <p className="text-xs text-[#9CA3AF] mt-1">Séparés par virgule, point-virgule ou retour à la ligne</p>
          </div>

          <div>
            <Label>Message personnalisé (optionnel)</Label>
            <Textarea rows={3} placeholder="Bonjour, ci-joint le compte rendu de la visite du…"
              value={message} onChange={e => setMessage(e.target.value)} />
          </div>

          <div className="flex gap-2 pt-2">
            <Button variant="outline" onClick={onClose} className="flex-1">Annuler</Button>
            <Button onClick={send} disabled={sending} className="flex-1 bg-[#0055FF] hover:bg-blue-700" data-testid="send-visit-btn">
              {sending ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Mail size={14} className="mr-1" />}
              Envoyer ({selected.size + extra.split(/[,;\n]/).filter(s => s.trim().includes('@')).length})
            </Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
